import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { Hidden } from '@material-ui/core';
import { useHistory } from 'react-router';
import Loader from '@cpg-its/ads.common.loading-circle';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { FormField } from '../../../../global/components/form-element/form-field';
import { AddressField } from '../../../../global/components/form-element/address';
import { InstitutionAddRequestContactInformationFields, InstitutionAddRequestMailingAddressFieldNames, InstitutionAddRequestPhysicalAddressFieldNames } from '../../../account-info/models/form-models';
import { getAddressLabels } from '../../../account-info/selectors/form-selectors';
import { GetInstitutionDetailMailingAddressRequiredSchema } from './institution-form-model';
import { getFormatedPhone } from '../../../../global/helpers/transformer';
import { setInstitutionAddRequestStatus } from './institution-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { postAddOrganizationRequest } from '../../../../global/ducks/organization-prc-api';
import { useInstitutionSetting } from '../../components/use-institution-settings';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { LabelSettingProps, TcsmContentPropsState } from '../../../../global/models/epi-model';
import { ContactInformation } from './component/contact-information';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { displayQuestionOptions, GetInstitutionAddRequestValidationSchema, InstitutionAddRequest } from './institution-add-request-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { parseLov, parsePhone } from '../../../../global/helpers/form-data-helper';
import { InstitutionProps } from '../../models/arc-epi-props';
import { InstitutionState } from './institution-model';
import { GetAddResponseRedirectUrl } from './managed-institution-helper';
import { useDioceseList } from '../../hooks/useDioceseList';
import { Other } from '../../../clergy-bio/education/clergy-bio-education-item';
import { YesNoOptionsFull } from '../../../../global/constants/lists';
import { sortObj } from '../../../../global/helpers/sort-helper';

function InstitutionAddRequestApp() {
	const app = useContext(DemographicsContext);
	const history = useHistory();
	const labelSettingState = useSelector<TcsmStates, TcsmContentPropsState<LabelSettingProps>>(state => state.epiProps?.labelSettingState);
	const institutionSettingsState = useSelector<TcsmStates, TcsmContentPropsState<InstitutionProps>>(state => state.epiProps?.institutionSettingsState);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);

	const institutionSettings = useInstitutionSetting();
	const dispatch = useDispatch();
	const gpsItem = getGpsType(GpsType.INSTITUTION_ADD_REQUEST, app);
	const dioListLoaded = useDioceseList();
	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);

	const institutionTypes = useSelector<TcsmStates, any>(state => state.lovProcState?.orgLov?.org_type);
	const institutionTypesOptions = institutionTypes ? parseLov(institutionTypes) : [];


	const validationSchema = GetInstitutionAddRequestValidationSchema(labelSettings, institutionSettings, institutionState?.dioceseList?.length > 1);



	let initialInstitution: InstitutionAddRequest = {
		legal_name: '',
		common_name: '',
		institution_type: '',
		institution_type_other: '',
		tec_org: '',
		year_established: '',
		reason: '',
		relationship_start_date: null,
		mail_address1: '',
		mail_address2: '',
		mail_address3: '',
		mail_city: '',
		mail_state: '',
		mail_postal_code: '',
		mail_country: '',
		street_address1: '',
		street_address2: '',
		street_address3: '',
		street_city: '',
		street_state: '',
		street_postal_code: '',
		street_country: '',
		office_country_code: '',
		office_phone: '',
		office_ext: '',
		website: '',
		email: '',
		office_phone_formatted: '',
		office_phone_number: '',
		office_phone_area_code: '',
		fax_country_code: '',
		fax_phone_area_code: '',
		fax_phone_formatted: '',
		fax_phone_number: '',
		dio: institutionState?.dioceseList?.length === 1 ? institutionState.dioceseList[0]?.client_number : '',
	};

	const apiErrors = useApiResponseError();

	initialInstitution = {
		...initialInstitution,
		office_phone_formatted: getFormatedPhone(initialInstitution.office_phone_area_code,
			initialInstitution.office_phone_number),
		fax_phone_formatted: getFormatedPhone(initialInstitution.fax_phone_area_code, initialInstitution.fax_phone_number),
	};

	const requestSubmittedPath = useBasePath(GetAddResponseRedirectUrl());

	function OnSuccess() {
		dispatch(setInstitutionAddRequestStatus(ApiStatuses.Success));
		history.push(requestSubmittedPath);
	}

	function OnFailure() {
		// Error.       
	}

	function CreateInstitutionRequestPayload(addRequest: InstitutionAddRequest) {

		const copy = { ...addRequest };
		const payload: any = {
			dio_client_number: copy.dio,
		};

		delete copy.dio;

		payload.organization = copy;

		return payload;
	}

	const onSubmitHandler = (update: InstitutionAddRequest) => {
		dispatch(apiErrorClear());
		dispatch((setInstitutionAddRequestStatus(ApiStatuses.None)));


		dispatch(postAddOrganizationRequest(CreateInstitutionRequestPayload(parsePhone(update)), OnSuccess, OnFailure));
	};

	if (!labelSettingState?.contentLoaded || !institutionSettingsState?.contentLoaded || !dioListLoaded) {
		return (<Loader />);
	}

	return (
		<>
			<Formik
				initialValues={initialInstitution}
				onSubmit={(values) => onSubmitHandler(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}>
				{(formProps: FormikProps<InstitutionAddRequest>) => (
					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings.submitText}
							cancelText={labelSettings.cancelButtonText}
							onCancel={() => { history.goBack(); }}
						>
							<PageHeader
								headerText={institutionSettings?.institutionAddRequestTitle}
								description={institutionSettings?.institutionAddRequestDescription} />

							<ContentSpacer>
								<h2>{institutionSettings?.institutionAddRequestSubtitle}</h2>
							</ContentSpacer>
							<Hidden mdUp>
								<SideBarContent>
									<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
								</SideBarContent>
							</Hidden>
							<DividerSection>
								<FormGroup>
									<ContentSpacer>

										<FormField
											name='legal_name'
											label={labelSettings?.institutionLegalName}
											component='input'
											errors={formProps.errors}
											touched={formProps.touched}
											tooltip={{ title: labelSettings?.institutionLegalNameTooltip }}
											required
										/>
										<FormField
											name='common_name'
											label={labelSettings?.institutionName}
											tooltip={{ title: labelSettings?.institutionNameTooltip }}
											component='input'
											errors={formProps.errors}
											touched={formProps.touched}
											required
										/>
									</ContentSpacer>
									<ContentSpacer>
										{
											institutionState?.dioceseList?.length === 1 ?
												<>
													<p><strong>{institutionSettings.dioceseLabel}</strong></p>
													<p>{institutionState?.dioceseList[0]?.diocese}</p>
												</>
												:

												<FormField
													name='dio'
													label={institutionSettings.dioceseLabel}
													component='input'
													options={sortObj(institutionState?.dioceseList?.map(d => { return { value: d.client_number, text: d.diocese }; }), 'text', true)}
													errors={formProps.errors}
													touched={formProps.touched}
													required
													select
													autoWidth
												/>
										}
									</ContentSpacer>
									<ContentSpacer>
										<FormField
											name='institution_type'
											label={institutionSettings.institutionAddRequestInstitutionTypeLabel}
											tooltip={{ title: institutionSettings?.institutionAddRequestInstitutionTypeTooltip }}
											component='input'
											options={institutionTypesOptions}
											errors={formProps.errors}
											touched={formProps.touched}
											select
											required
										/>
									</ContentSpacer>

									{
										formProps?.values?.institution_type === Other &&
										<div>
											<ContentSpacer>
												<FormField
													name='institution_type_other'
													label={institutionSettings?.contributionOtherLabel}
													component='input'
													rows={4}
													errors={formProps.errors}
													touched={formProps.touched}
													multiline
													required
												/>
												</ContentSpacer>																					
										</div>

									}
									{
										displayQuestionOptions.includes(formProps?.values?.institution_type) &&
										<ContentSpacer>
											<FormField
												name='tec_org'
												label={institutionSettings.isTecLabel}
												component='radioGroup'
												options={YesNoOptionsFull}
												errors={formProps.errors}
												touched={formProps.touched}
												tooltip={{
													title: institutionSettings.isTecHelperText,
												}}
												required
											/>
										</ContentSpacer>
									}
									<FormField
										name='year_established'
										label={institutionSettings?.yearEstablishedLabel}
										component='input'
										errors={formProps.errors}
										touched={formProps.touched}
										tooltip={{ title: institutionSettings?.yearEstablishedHelperText }}
										required
										autoWidth
									/>
									<ContentSpacer>
										<p>{institutionSettings.yearEstablishedFormat}</p>
									</ContentSpacer>
									<FormField
										name='relationship_start_date'
										label={institutionSettings.institutionAddRequestRelationshipStartDateLabel}
										component='datePicker'
										errors={formProps.errors}
										touched={formProps.touched}
										required
										tooltip={{
											title: institutionSettings.relationshipStartDateHelperText,
										}}
										autoWidth
									/>
									<FormField
										name='reason'
										label={institutionSettings?.institutionAddRequestOtherInformationLabel}
										component='input'
										rows={4}
										errors={formProps.errors}
										touched={formProps.touched}
										multiline
									/>
								</FormGroup>
							</DividerSection>

							<DividerSection sectionText={institutionSettings?.institutionAddRequestMailingLabel}>
								<AddressField
									currentCountry={formProps.values.mail_country}
									labels={getAddressLabels(labelSettings)}
									addressFieldNames={InstitutionAddRequestMailingAddressFieldNames}
									formProps={formProps}
									requiredSchema={GetInstitutionDetailMailingAddressRequiredSchema(formProps.values.mail_country)}
								/>
							</DividerSection>
							<DividerSection sectionText={labelSettings?.physicalAddress}>
								<AddressField
									currentCountry={formProps.values.street_country}
									labels={getAddressLabels(labelSettings)}
									addressFieldNames={InstitutionAddRequestPhysicalAddressFieldNames}
									formProps={formProps}
									requiredSchema={GetInstitutionDetailMailingAddressRequiredSchema(formProps.values.street_country)}
								/>
							</DividerSection>
							<DividerSection removeDivider sectionText={labelSettings?.contactInformation}>
								<ContactInformation labelSettings={labelSettings} formProps={formProps} contactInformationFields={InstitutionAddRequestContactInformationFields} />
							</DividerSection>
						</FormWrapper>
					</Form>
				)}
			</Formik>
		</>
	);
}

export default InstitutionAddRequestApp;